import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface ExecutiveSummaryEditorProps {
  summary: string;
  onChange: (summary: string) => void;
}

export const ExecutiveSummaryEditor: React.FC<ExecutiveSummaryEditorProps> = ({ 
  summary, 
  onChange 
}) => {
  const { isDark } = useTheme();
  
  // Debug logging
  console.log('ExecutiveSummaryEditor received summary:', summary);
  console.log('ExecutiveSummaryEditor summary length:', summary?.length || 0);
  
  const textareaClass = `w-full px-4 py-3 rounded-lg ${
    isDark 
      ? 'bg-white/[0.05] border-white/10 text-white placeholder-slate-400' 
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
  } border focus:outline-none focus:ring-2 focus:ring-professional-blue-500 min-h-[400px] resize-y`;
  
  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? 'text-slate-300' : 'text-gray-700'
  }`;
  
  const helpTextClass = `text-sm mt-2 ${
    isDark ? 'text-slate-400' : 'text-gray-600'
  }`;

  // Default template text for new scenarios
  const defaultTemplate = `Your Role:
As Chief Executive Officer of [Company Name], you lead a Fortune 500 technology company with operations spanning 45 countries and annual revenues exceeding $12 billion. Your leadership has driven consistent growth over the past five years, establishing your company as an industry leader in cloud computing and enterprise software solutions.

Current Business Context:
The Board of Directors has set ambitious targets for the next fiscal year:
• Geographic expansion into emerging markets
• Integration of advanced AI capabilities across all product lines
• 20% revenue growth while maintaining current profit margins
• Digital transformation of legacy infrastructure systems

These strategic initiatives require significant technology investments and expanded digital infrastructure, substantially increasing your organization's cyber risk exposure.

Your Challenge:
Balance aggressive growth objectives with prudent risk management. Every decision you make will impact your company's security posture, financial performance, and stakeholder confidence. The Board expects you to deliver growth while protecting the organization from potentially catastrophic cyber incidents.

This simulation will test your ability to navigate complex risk scenarios, make strategic decisions under pressure, and allocate resources effectively to protect your organization's assets and reputation.`;

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className={labelClass} htmlFor="executive-summary">
            Executive Summary
          </label>
          {!summary && (
            <button
              type="button"
              onClick={() => onChange(defaultTemplate)}
              className={`text-sm px-3 py-1 rounded-md transition-colors ${
                isDark 
                  ? 'bg-professional-blue-600/20 text-professional-blue-400 hover:bg-professional-blue-600/30' 
                  : 'bg-professional-blue-100 text-professional-blue-600 hover:bg-professional-blue-200'
              }`}
            >
              Use Default Template
            </button>
          )}
        </div>
        <textarea
          id="executive-summary"
          value={summary}
          onChange={(e) => onChange(e.target.value)}
          className={textareaClass}
          placeholder="Enter the executive briefing content..."
          rows={20}
        />
        <p className={helpTextClass}>
          This content will be displayed as the executive briefing when the scenario starts. 
          Include sections for role, business context, and challenge. The cybersecurity landscape 
          will be displayed separately in the game.
        </p>
      </div>
    </div>
  );
};